import { useCallback, useEffect, useMemo, useState } from "react";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import Footer from "../component/Footer";
import axios from "axios";
import { BASE_URL } from "../api";
import Loader from "../component/Loader";
import MessageToster from "../component/MessageToster";
import { getApiHeaders, formatDateDDMMYYYY } from "../utils/helpers";
import { useHandleError } from "../hooks/useHandleError";
import CommonTable from "../component/CommonTable";
import ExportButton from "../component/ExportButton";
import { exportToExcel } from "../utils/exportData";

// API returns [[{...}]], so flatten to a clean array of row objects
const toRowList = (list) => {
    if (!list) return [];
    if (typeof list === "string") {
        try {
            list = JSON.parse(list);
        } catch {
            return [];
        }
    }
    if (Array.isArray(list)) {
        return list.flat(Infinity).filter((row) => row && typeof row === "object");
    }
    if (typeof list === "object") {
        const possible =
            list.list ||
            list.records ||
            list.sub_list ||
            list.ib_sub_list ||
            list.data;
        if (Array.isArray(possible)) {
            return possible.flat(Infinity).filter((row) => row && typeof row === "object");
        }
    }
    return [];
};

export default function IbSubCommissionReport() {
    const handleError = useHandleError();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);

    const today = new Date().toISOString().split("T")[0];
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedLevel, setSelectedLevel] = useState("all");
    const [selectedStatus, setSelectedStatus] = useState("all");

    // Fetch IB Sub Commission List
    const fetchIbSubList = useCallback(async () => {
        setLoading(true);
        try {
            let res;
            try {
                // Try GET /ib/ib_sub_list
                res = await axios.get(`${BASE_URL}/ib/ib_sub_list`, {
                    headers: getApiHeaders(),
                });
            } catch (err) {
                if (err.response?.status === 404) {
                    try {
                        // Fallback to /IB/ib_sub_list (uppercase IB)
                        res = await axios.get(`${BASE_URL}/IB/ib_sub_list`, {
                            headers: getApiHeaders(),
                        });
                    } catch (err2) {
                        if (err2.response?.status === 405) {
                            res = await axios.post(`${BASE_URL}/IB/ib_sub_list`, {}, {
                                headers: getApiHeaders(),
                            });
                        } else {
                            throw err2;
                        }
                    }
                } else if (err.response?.status === 405) {
                    res = await axios.post(`${BASE_URL}/ib/ib_sub_list`, {}, {
                        headers: getApiHeaders(),
                    });
                } else {
                    throw err;
                }
            }

            if (res && res.data) {
                const rows = toRowList(res.data.data !== undefined ? res.data.data : res.data);
                setData(rows);

                if (
                    res.data.code &&
                    res.data.code !== 200 &&
                    res.data.code !== "200" &&
                    res.data.status !== "success" &&
                    res.data.status !== true
                ) {
                    if (res.data.message) {
                        MessageToster("error", res.data.message);
                    }
                }
            } else {
                setData([]);
            }
        } catch (error) {
            setData([]);
            handleError(error);
        } finally {
            setLoading(false);
        }
    }, [handleError]);

    useEffect(() => {
        fetchIbSubList();
    }, [fetchIbSubList]);

    // Unique levels from data for filter dropdown
    const availableLevels = useMemo(() => {
        const levels = new Set();
        data.forEach((r) => {
            if (r.downline_level != null) {
                levels.add(r.downline_level);
            }
        });
        return Array.from(levels).sort((a, b) => Number(a) - Number(b));
    }, [data]);

    // Filter data based on filters
    const filteredData = useMemo(() => {
        return data.filter((item) => {
            const rawDate = item.joiningdate || item.created_at || item.date;

            // Date filtering
            if (fromDate) {
                const itemDate = new Date(rawDate);
                const from = new Date(fromDate);
                from.setHours(0, 0, 0, 0);
                if (!isNaN(itemDate.getTime()) && itemDate < from) return false;
            }

            if (toDate) {
                const itemDate = new Date(rawDate);
                const to = new Date(toDate);
                to.setHours(23, 59, 59, 999);
                if (!isNaN(itemDate.getTime()) && itemDate > to) return false;
            }

            // Downline level filter
            if (selectedLevel !== "all") {
                if (String(item.downline_level) !== String(selectedLevel)) return false;
            }

            // Status filter
            if (selectedStatus !== "all") {
                const itemStatus = String(item.ib_status || "").toLowerCase();
                if (itemStatus !== selectedStatus.toLowerCase()) return false;
            }

            // Search term
            if (searchTerm.trim()) {
                const term = searchTerm.toLowerCase();
                const fullName = `${item.fname || ""} ${item.lname || ""}`.toLowerCase();
                const loginId = String(item.loginid || "").toLowerCase();
                const email = String(item.email || "").toLowerCase();
                const phone = String(item.phone || "").toLowerCase();
                const country = String(item.country || "").toLowerCase();

                if (
                    !fullName.includes(term) &&
                    !loginId.includes(term) &&
                    !email.includes(term) &&
                    !phone.includes(term) &&
                    !country.includes(term)
                ) {
                    return false;
                }
            }

            return true;
        });
    }, [data, fromDate, toDate, selectedLevel, selectedStatus, searchTerm]);

    // Summary statistics for top cards
    const stats = useMemo(() => {
        const totalSubIbs = filteredData.length;

        const totalCommission = filteredData.reduce((acc, r) => {
            const val = Number(r.total_commission_received || 0);
            return acc + (isNaN(val) ? 0 : val);
        }, 0);

        const totalLiveAccounts = filteredData.reduce((acc, r) => {
            const val = Number(r.total_live_accounts || 0);
            return acc + (isNaN(val) ? 0 : val);
        }, 0);

        const totalDeposit = filteredData.reduce((acc, r) => {
            const val = Number(r.total_deposit || 0);
            return acc + (isNaN(val) ? 0 : val);
        }, 0);

        return { totalSubIbs, totalCommission, totalLiveAccounts, totalDeposit };
    }, [filteredData]);

    // Clear filters
    const clearFilters = () => {
        setFromDate("");
        setToDate("");
        setSelectedLevel("all");
        setSelectedStatus("all");
        setSearchTerm("");
    };

    // Export to Excel
    const handleExport = () => {
        if (!filteredData || filteredData.length === 0) return;
        const exportRows = filteredData.map((row, idx) => ({
            "No.": idx + 1,
            "Login ID": row.loginid || "-",
            "First Name": row.fname || "-",
            "Last Name": row.lname || "-",
            "Email": row.email || "-",
            "Phone": row.phone
                ? (row.country_code ? `+${row.country_code} ${row.phone}` : row.phone)
                : "-",
            "Country": row.country || "-",
            "Downline Level": row.downline_level ?? "-",
            "Commission / Lot":
                row.available_commission_per_lot ?? row.commision_per ?? 0,
            "Live Accounts": row.total_live_accounts ?? 0,
            "Total Deposit": row.total_deposit ?? 0,
            "Commission Received": row.total_commission_received ?? 0,
            "Joining Date": formatDateDDMMYYYY(row.joiningdate),
            "Status": row.ib_status || "Active",
        }));
        exportToExcel(exportRows, "IB_Sub_Commission_Report", "default");
    };

    // Table Columns
    const columns = [
        {
            header: "IB User",
            render: (row) => {
                const name = `${row.fname || ""} ${row.lname || ""}`.trim() || "-";
                const initials =
                    (row.fname?.[0] || "I").toUpperCase() +
                    (row.lname?.[0] || "B").toUpperCase();
                return (
                    <div className="d-flex align-items-center">
                        <div className="avatar-xs me-2 flex-shrink-0">
                            <span className="avatar-title rounded-circle bg-primary-subtle text-primary font-size-12 fw-semibold">
                                {initials}
                            </span>
                        </div>
                        <div>
                            <h5 className="font-size-13 mb-0 text-dark fw-semibold">{name}</h5>
                            <p className="text-muted font-size-11 mb-0">{row.email || "-"}</p>
                        </div>
                    </div>
                );
            },
        },
        {
            header: "Login ID",
            render: (row) => (
                <span className="badge bg-light text-dark border font-monospace font-size-12 px-2 py-1">
                    {row.loginid || "-"}
                </span>
            ),
        },
        {
            header: "Level",
            render: (row) => (
                <span className="badge bg-primary bg-opacity-10 text-primary px-2 py-1">
                    Level {row.downline_level ?? "-"}
                </span>
            ),
        },
        {
            header: "Country & Phone",
            render: (row) => {
                const phone = row.phone
                    ? row.country_code
                        ? `+${row.country_code} ${row.phone}`
                        : row.phone
                    : null;
                return (
                    <div>
                        <div className="font-size-12 text-dark fw-medium">{row.country || "-"}</div>
                        {phone && <div className="text-muted font-size-11">{phone}</div>}
                    </div>
                );
            },
        },
        {
            header: "Commission / Lot",
            render: (row) => {
                const comm = row.available_commission_per_lot ?? row.commision_per;
                if (comm == null || comm === "") return "-";
                const num = Number(comm);
                return (
                    <span className="font-monospace text-dark">
                        ${isNaN(num) ? comm : num.toFixed(2)}
                    </span>
                );
            },
        },
        {
            header: "Total Deposit",
            render: (row) => {
                const dep = Number(row.total_deposit || 0);
                return (
                    <span className="font-monospace text-dark">
                        ${dep.toFixed(2)}
                    </span>
                );
            },
        },
        {
            header: "Commission Earned",
            render: (row) => {
                const comm = Number(row.total_commission_received || 0);
                return (
                    <span className="fw-bold text-success font-monospace">
                        ${comm.toFixed(2)}
                    </span>
                );
            },
        },
        {
            header: "Joining Date",
            render: (row) => formatDateDDMMYYYY(row.joiningdate),
        },
        {
            header: "Status",
            render: (row) => {
                const status = String(row.ib_status || "Active").toLowerCase();
                const isSuccess = status === "active" || status === "approved";
                const isPending = status === "pending";
                return (
                    <span
                        className={`badge px-2 py-1 ${isSuccess
                            ? "bg-success bg-opacity-10 text-success"
                            : isPending
                                ? "bg-warning bg-opacity-10 text-warning"
                                : "bg-danger bg-opacity-10 text-danger"
                            }`}
                    >
                        {row.ib_status || "Active"}
                    </span>
                );
            },
        },
    ];

    return (
        <div id="layout-wrapper">
            {loading && <Loader />}
            <Topbar />
            <Sidebar />
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        {/* Page Header */}
                        <div className="row">
                            <div className="col-12">
                                <div className="page-title-box d-sm-flex align-items-center justify-content-between mb-3">
                                    <h2 className="fw-700 mb-sm-0">IB Sub Commission Report</h2>
                                </div>
                            </div>
                        </div>

                        {/* Summary Cards */}
                        <div className="row mb-3">
                            <div className="col-xxl-3 col-md-6 col-sm-6 mb-3">
                                <div className="card h-100 mb-0 shadow-sm border-0">
                                    <div className="card-body p-3">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-grow-1">
                                                <p className="text-muted fw-medium text-uppercase font-size-12 mb-1">
                                                    Total Sub IBs
                                                </p>
                                                <h3 className="mb-0 fw-bold text-primary">
                                                    {stats.totalSubIbs}
                                                </h3>
                                            </div>
                                            <div className="avatar-sm flex-shrink-0 ms-2">
                                                <span className="avatar-title rounded-circle bg-primary bg-opacity-10 text-primary font-size-22">
                                                    <i className="bx bx-group" />
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-xxl-3 col-md-6 col-sm-6 mb-3">
                                <div className="card h-100 mb-0 shadow-sm border-0">
                                    <div className="card-body p-3">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-grow-1">
                                                <p className="text-muted fw-medium text-uppercase font-size-12 mb-1">
                                                    Total Commission
                                                </p>
                                                <h3 className="mb-0 fw-bold text-success">
                                                    ${Number(stats.totalCommission).toFixed(2)}
                                                </h3>
                                            </div>
                                            <div className="avatar-sm flex-shrink-0 ms-2">
                                                <span className="avatar-title rounded-circle bg-success bg-opacity-10 text-success font-size-22">
                                                    <i className="bx bx-dollar-circle" />
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-xxl-3 col-md-6 col-sm-6 mb-3">
                                <div className="card h-100 mb-0 shadow-sm border-0">
                                    <div className="card-body p-3">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-grow-1">
                                                <p className="text-muted fw-medium text-uppercase font-size-12 mb-1">
                                                    Total Live Accounts
                                                </p>
                                                <h3 className="mb-0 fw-bold text-info">
                                                    {stats.totalLiveAccounts}
                                                </h3>
                                            </div>
                                            <div className="avatar-sm flex-shrink-0 ms-2">
                                                <span className="avatar-title rounded-circle bg-info bg-opacity-10 text-info font-size-22">
                                                    <i className="bx bx-user-check" />
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-xxl-3 col-md-6 col-sm-6 mb-3">
                                <div className="card h-100 mb-0 shadow-sm border-0">
                                    <div className="card-body p-3">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-grow-1">
                                                <p className="text-muted fw-medium text-uppercase font-size-12 mb-1">
                                                    Total Deposits
                                                </p>
                                                <h3 className="mb-0 fw-bold text-warning">
                                                    ${Number(stats.totalDeposit).toFixed(2)}
                                                </h3>
                                            </div>
                                            <div className="avatar-sm flex-shrink-0 ms-2">
                                                <span className="avatar-title rounded-circle bg-warning bg-opacity-10 text-warning font-size-22">
                                                    <i className="bx bx-wallet" />
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Table Card with Filter Bar */}
                        <div className="row">
                            <div className="col-12">
                                <div className="card">
                                    <div className="card-body">
                                        {/* Filter Bar */}
                                        <div className="d-flex flex-wrap align-items-end gap-2 mb-4">
                                            <div>
                                                <label className="form-label mb-1">From Date</label>
                                                <input
                                                    type="date"
                                                    className="form-control form-control-sm"
                                                    value={fromDate}
                                                    max={toDate || today}
                                                    onChange={(e) => setFromDate(e.target.value)}
                                                />
                                            </div>
                                            <div>
                                                <label className="form-label mb-1">To Date</label>
                                                <input
                                                    type="date"
                                                    className="form-control form-control-sm"
                                                    value={toDate}
                                                    min={fromDate || undefined}
                                                    max={today}
                                                    onChange={(e) => setToDate(e.target.value)}
                                                />
                                            </div>

                                            {availableLevels.length > 0 && (
                                                <div>
                                                    <label className="form-label mb-1">Level</label>
                                                    <select
                                                        className="form-select form-select-sm"
                                                        value={selectedLevel}
                                                        onChange={(e) => setSelectedLevel(e.target.value)}
                                                    >
                                                        <option value="all">All Levels</option>
                                                        {availableLevels.map((lvl) => (
                                                            <option key={lvl} value={lvl}>
                                                                Level {lvl}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            )}

                                            <div>
                                                <label className="form-label mb-1">Status</label>
                                                <select
                                                    className="form-select form-select-sm"
                                                    value={selectedStatus}
                                                    onChange={(e) => setSelectedStatus(e.target.value)}
                                                >
                                                    <option value="all">All Status</option>
                                                    <option value="active">Active</option>
                                                    <option value="pending">Pending</option>
                                                    <option value="inactive">Inactive</option>
                                                </select>
                                            </div>

                                            <div>
                                                <label className="form-label mb-1">Search</label>
                                                <input
                                                    type="search"
                                                    className="form-control form-control-sm"
                                                    placeholder="Search IB, ID, email..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                />
                                            </div>

                                            <div>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-secondary waves-effect"
                                                    onClick={clearFilters}
                                                    disabled={
                                                        !fromDate &&
                                                        !toDate &&
                                                        selectedLevel === "all" &&
                                                        selectedStatus === "all" &&
                                                        !searchTerm
                                                    }
                                                >
                                                    Clear
                                                </button>
                                            </div>

                                            <div>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-primary waves-effect"
                                                    onClick={fetchIbSubList}
                                                    disabled={loading}
                                                    title="Refresh Data"
                                                >
                                                    <i className={`bx bx-refresh me-1 ${loading ? "bx-spin" : ""}`} />
                                                    Refresh
                                                </button>
                                            </div>

                                            <div className="ms-auto">
                                                <ExportButton
                                                    onClick={handleExport}
                                                    disabled={!filteredData || filteredData.length === 0}
                                                />
                                            </div>
                                        </div>

                                        {/* Records Table */}
                                        <CommonTable
                                            columns={columns}
                                            data={filteredData}
                                            loading={loading}
                                            noDataMessage="No sub commission records found"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>
        </div>
    );
}
